import { Overlay } from '@angular/cdk/overlay';
import { Component } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FormControlsOf } from '@y42/angular/forms';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { DatepickerRange } from '@y42/core/daterange-picker';
import { DateTimeService } from '@y42/date-time';
import { ColumnTypeEnum } from '@y42/json-schema';
import { isNullish } from '@y42/util/nullish';
import { IFilterAngularComp } from 'ag-grid-angular';
import { IDoesFilterPassParams, IFilterParams, ValueGetterParams } from 'ag-grid-community';
import { BehaviorSubject, distinctUntilChanged, map, startWith } from 'rxjs';
import { NonClosableOverlayService } from '../../services/non-closable-overlay.service';

const operators = [
  // value is  import { ISimpleFilterModelType } from 'ag-grid-community/dist/lib/filter/provided/simpleFilter';
  { value: 'equals', text: 'Equals' },
  { value: 'notEqual', text: 'Not Equal' },
  { value: 'lessThan', text: 'Less Than' },
  { value: 'greaterThan', text: 'Greater Than' },
  { value: 'inRange', text: 'Between' },
] as const;

type OperatorType = (typeof operators)[number]['value'];

interface FilterForm {
  type: OperatorType | 'blank';
  value: Date | null;
  value_between: Date | null;
}

@UntilDestroy()
@Component({
  selector: 'y42-date-client-side-filter',
  templateUrl: './date-client-side-filter.component.html',
  styleUrls: ['./date-client-side-filter.component.scss'],
  providers: [
    {
      provide: Overlay,
      useClass: NonClosableOverlayService,
    },
  ],
})
export class DateClientSideFilterComponent implements IFilterAngularComp {
  constructor(private readonly dateTimeService: DateTimeService) {}

  private readonly params$$ = new BehaviorSubject<IFilterParams | null>(null);

  // if you want to use datepicker with (hour, minute) selector
  // define in column definitions
  // cellRendererParams: {
  //   type: ColumnTypeEnum.Datetime || ColumnTypeEnum.Timestamp,
  // },
  private readonly filterType$$ = this.params$$.pipe(
    map((params) => params?.column.getColDef().cellRendererParams?.type || ColumnTypeEnum.Date),
    startWith(ColumnTypeEnum.Date),
  );

  readonly operators = operators;
  readonly trackByValue = getTrackByFn<this['operators'][number]>('value');

  readonly form = new FormGroup<FormControlsOf<FilterForm>>({
    type: new FormControl('equals', { validators: [Validators.required], nonNullable: true }),
    value: new FormControl<Date | null>(null, { validators: [Validators.required] }),
    value_between: new FormControl<Date | null>(null),
  });

  readonly state = connectState(this, {
    isFilterActive: this.form.valueChanges.pipe(
      map(() => !!this.form.getRawValue().value),
      distinctUntilChanged(),
      startWith(false),
    ),
    filterType: this.filterType$$,
    hasTimePicker: this.filterType$$.pipe(
      map((filterType) => [ColumnTypeEnum.Datetime, ColumnTypeEnum.Timestamp].includes(filterType)),
    ),
  });

  agInit(params: IFilterParams): void {
    this.params$$.next(params);
  }

  isFilterActive(): boolean {
    return this.state.isFilterActive;
  }

  doesFilterPass(params: IDoesFilterPassParams): boolean {
    const formValue = this.form.getRawValue();
    const selectedDate = formValue.value;
    const selectedEndDate = formValue.value_between;

    if (selectedDate === null) {
      return false;
    }

    const dataValue = this.params$$.value?.valueGetter(params as ValueGetterParams);
    const checkDate = isNullish(dataValue) ? null : new Date(dataValue);

    if (formValue.type === 'equals' && checkDate === null) {
      return selectedDate === checkDate;
    }

    if (formValue.type === 'blank') {
      return checkDate === null;
    }

    if (checkDate === null) {
      return false;
    }

    const granularity: 'hours' | 'days' = [ColumnTypeEnum.Datetime, ColumnTypeEnum.Timestamp].includes(
      this.state.filterType,
    )
      ? 'hours'
      : 'days';

    const typeResolver: Record<OperatorType, () => boolean> = {
      equals: () => this.dateTimeService.isSame(checkDate, selectedDate, granularity),
      notEqual: () => !this.dateTimeService.isSame(checkDate, selectedDate, granularity),
      greaterThan: () => this.dateTimeService.isAfter(checkDate, selectedDate, granularity),
      lessThan: () => this.dateTimeService.isBefore(checkDate, selectedDate, granularity),
      inRange: () => {
        if (selectedDate === null || selectedEndDate === null) {
          return checkDate === selectedDate || checkDate === selectedEndDate;
        } else {
          return this.dateTimeService.isBetween(checkDate, selectedDate, selectedEndDate, 'days');
        }
      },
    };

    return typeResolver?.[formValue.type]?.() || false;
  }

  getModel(): FilterForm | null {
    const rawData = this.form.getRawValue();
    return rawData.value ? this.parseModel(rawData) : null;
  }

  setModel(model: { type: OperatorType; value?: unknown; values?: unknown[] } | null): void {
    const valueExtracted = model?.value ?? model?.values?.[0];

    const mappedModel = model
      ? {
          type: model.type,
          value: valueExtracted ? this.dateTimeService.parseDate(valueExtracted as string) : null,
          value_between: null,
        }
      : null;

    if (mappedModel) {
      this.form.patchValue(mappedModel);
    } else {
      this.form.reset();
    }
    this.params$$.value?.filterChangedCallback();
  }

  setDates({ start, end }: DatepickerRange): void {
    const endValue = this.form.controls.type.value === 'inRange' ? end : null;
    this.form.patchValue({ value: start, value_between: endValue });
  }

  onFilterApply() {
    this.params$$.value?.filterChangedCallback();
  }

  onOperatorChange(value: OperatorType) {
    if (value === 'inRange') {
      this.form.controls.value_between.setValidators([Validators.required]);
      this.form.patchValue({
        value_between: null,
      });
    } else {
      this.form.controls.value_between.clearValidators();
    }
    this.form.controls.value_between.updateValueAndValidity();
  }

  onFilterReset() {
    this.form.reset();
    this.params$$.value?.filterChangedCallback();
  }

  private parseModel(values: FilterForm) {
    if (values.value === null || (values.value_between === null && values.type === 'inRange')) {
      values.type = 'blank';
    }

    return values;
  }
}
